import React, { useState, useEffect } from 'react';
import { getContactData, getStaticContent } from '../services/api';
import { 
  ArrowRight, 
  MapPin, 
  Mail, 
  Phone, 
  MessageCircle, 
  Instagram, 
  Share2 
} from 'lucide-react';

// --- Components ---

const SectionTag = ({ children }) => (
  <div className="flex items-center gap-2 mb-6">
    <div className="w-8 h-[2px] bg-blue-600"></div>
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-blue-600 italic">
      {children}
    </span>
  </div>
);

const ContactItem = ({ label, value, icon: Icon, action, isLink = false, actionLabel }) => (
  <div 
    onClick={action}
    className={`group border-t border-zinc-200 py-8 cursor-pointer transition-colors hover:bg-zinc-50 ${isLink ? 'cursor-pointer' : ''}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex gap-6">
        <div className="text-zinc-400 group-hover:text-blue-600 transition-colors mt-1">
          <Icon size={20} />
        </div>
        <div>
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">{label}</h3>
          <p className="text-xl md:text-2xl font-medium text-zinc-800 group-hover:text-black transition-colors leading-tight max-w-md">
            {/* Hiding raw data, showing action label instead */}
            {actionLabel || 'Not Available'}
          </p>
        </div>
      </div>
      {isLink && (
        <ArrowRight className="text-zinc-300 group-hover:text-blue-600 transition-colors opacity-0 group-hover:opacity-100" />
      )}
    </div>
  </div>
);

const Contact = () => {
  const [data, setData] = useState({
    whatsapp: '',
    email: '',
    address: '',
    maps_embed: '',
    instagram: '',
    tiktok: ''
  });

  const [staticContent, setStaticContent] = useState({
    title: 'MARI BERKOLABORASI',
    description: 'Siap meningkatkan kualitas brand Anda dengan manufaktur premium? Kami hadir untuk mendiskusikan kebutuhan produksi Anda, dari sampling hingga distribusi massal.'
  });

  const [loading, setLoading] = useState(true);

  // --- SEO & Schema Markup ---
  useEffect(() => {
    // 1. Title & Meta Tags
    document.title = "Hubungi Kami - Lokasi & Kontak iMoonusel | Konveksi Bandung";
    
    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMeta("description", "Hubungi iMoonusel Konveksi untuk konsultasi produksi seragam. Dapatkan info lokasi pabrik, WhatsApp, dan email resmi kami.");
    setMeta("keywords", "kontak konveksi, alamat konveksi bandung, wa tukang jahit, lokasi imoonusel, email konveksi");

    // Open Graph
    const setOg = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setOg("og:title", "Hubungi Kami - Lokasi & Kontak iMoonusel | Konveksi Bandung");
    setOg("og:description", "Siap berkolaborasi? Temukan saluran komunikasi resmi kami disini.");
    setOg("og:image", "/logo512.png");
    setOg("og:url", window.location.href);
    setOg("og:type", "website");

    // 2. JSON-LD Schema Markup
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          "name": "Halaman Kontak iMoonusel",
          "description": "Informasi kontak dan lokasi workshop."
        },
        {
          "@type": "LocalBusiness",
          "name": "iMoonusel Konveksi",
          "image": "https://konveksiimoonusel.com/logo512.png",
          "telephone": "+62-898-7459-759",
          "email": "imoonuselkonveksi@gmail.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Girimukti, Batujajar",
            "addressLocality": "Kabupaten Bandung Barat",
            "addressRegion": "Jawa Barat",
            "postalCode": "40561",
            "addressCountry": "ID"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "-6.9", // Approximate, better if real data
            "longitude": "107.5"
          },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "09:00",
            "closes": "17:00"
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    fetchContactData();
    window.scrollTo(0, 0);
  }, []);
  const fetchContactData = async () => {
    try {
      const [contactRes, staticRes] = await Promise.all([
        getContactData(),
        getStaticContent('contact')
      ]);

      if (contactRes) {
        setData(prev => ({ ...prev, ...contactRes }));
      }
      
      if (staticRes) {
        setStaticContent({
          title: staticRes.contact_title?.id || 'MARI BERKOLABORASI',
          description: staticRes.contact_description?.id || 'Siap meningkatkan kualitas brand Anda dengan manufaktur premium? Kami hadir untuk mendiskusikan kebutuhan produksi Anda, dari sampling hingga distribusi massal.'
        });
      }

    } catch (err) {
      console.error("Failed to fetch contact data", err);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    if (!data.whatsapp) return;
    const phone = data.whatsapp.replace(/\D/g,'');
    const message = encodeURIComponent("Halo, saya ingin menanyakan tentang layanan konveksi.");
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const handleEmailClick = () => {
    if (!data.email) return;
    window.location.href = `mailto:${data.email}`;
  };

  const handlePhoneClick = () => {
    if (!data.whatsapp) return;
    const phone = data.whatsapp.replace(/^0/, '+62');
    window.location.href = `tel:${phone}`;
  };

  const handleSocialClick = (url) => {
    if (url) window.open(url, '_blank');
  };

  const renderMap = () => {
    const mapSource = data.maps_embed || data.address;

    if (!mapSource || typeof mapSource !== 'string') {
      return (
        <div className="w-full h-full min-h-[400px] bg-zinc-100 flex items-center justify-center text-zinc-400">
          <div className="text-center">
            <MapPin size={48} className="mx-auto mb-4 opacity-20" />
            <p className="text-sm font-bold uppercase tracking-widest">Location Data Unavailable</p>
          </div>
        </div>
      );
    }

    // Check if it's raw HTML/Iframe code
    if (mapSource.trim().startsWith('<')) {
      return (
        <div 
          className="w-full h-full min-h-[500px]"
          dangerouslySetInnerHTML={{ __html: mapSource }} 
        />
      );
    }

    // Otherwise, treat as an address string for Google Maps Embed
    const encodedAddress = encodeURIComponent(mapSource);
    return (
      <iframe 
        className="w-full h-full min-h-[500px] border-0"
        loading="lazy" 
        allowFullScreen 
        referrerPolicy="no-referrer-when-downgrade"
        src={`https://maps.google.com/maps?q=${encodedAddress}&t=k&z=15&ie=UTF8&iwloc=&output=embed`}
        title="Location Map"
      ></iframe>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-[10px] font-bold uppercase tracking-widest animate-pulse">Loading Data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black font-sans pt-20">
      
      {/* Hero Section */}
      <section className="relative pt-12 pb-24 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-8">
              <SectionTag>Contact Us</SectionTag>
              <h1 className="text-5xl md:text-9xl font-black tracking-tighter leading-[0.85] mb-8 break-words whitespace-pre-line uppercase">
                {staticContent.title}<span className="text-blue-600">.</span>
              </h1>
              <p className="text-xl text-zinc-600 max-w-2xl font-light leading-relaxed">
                {staticContent.description}
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col justify-end items-start md:items-end">
               <div className="bg-black text-white p-8 w-full max-w-sm">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-4">Quick Response</h3>
                  <button 
                    onClick={handleWhatsAppClick}
                    className="w-full bg-white text-black py-4 px-6 text-sm font-bold uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all flex items-center justify-between group"
                  >
                    Chat via WhatsApp
                    <MessageCircle size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-0">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-x-24 gap-y-0">
            
            {/* Left Column: Direct Contact */}
            <div className="py-12 md:py-24">
              <h2 className="text-3xl font-black tracking-tighter mb-12 uppercase">Saluran Komunikasi</h2>
              
              <ContactItem 
                label="WhatsApp Resmi" 
                value={data.whatsapp}
                actionLabel="Hubungi Kami di WhatsApp"
                icon={Phone} 
                action={handleWhatsAppClick}
                isLink
              />
              
              <ContactItem 
                label="Email Bisnis" 
                value={data.email}
                actionLabel="Kirim Penawaran via Email"
                icon={Mail} 
                action={handleEmailClick}
                isLink
              />

              <div className="flex gap-4 mt-8">
                 {data.instagram && (
                   <button 
                    onClick={() => handleSocialClick(data.instagram)}
                    className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest border border-zinc-200 px-6 py-3 hover:bg-black hover:text-white transition-all"
                   >
                     <Instagram size={14} /> Instagram
                   </button>
                 )}
                 {data.tiktok && (
                   <button 
                    onClick={() => handleSocialClick(data.tiktok)}
                    className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest border border-zinc-200 px-6 py-3 hover:bg-black hover:text-white transition-all"
                   >
                     <Share2 size={14} /> TikTok
                   </button>
                 )}
              </div>
            </div>

            {/* Right Column: Address */}
            <div className="py-12 md:py-24 md:border-l border-zinc-200 md:pl-24">
              <h2 className="text-3xl font-black tracking-tighter mb-12 uppercase">Pabrik & Kantor</h2>
              
              <div className="mb-12">
                <div className="flex items-start gap-4 mb-4 text-blue-600">
                  <MapPin size={24} />
                </div>
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-4">Lokasi Utama</h3>
                <p className="text-2xl font-light text-zinc-800 leading-relaxed">
                  {data.address}
                </p>
              </div>

              <div className="bg-zinc-50 p-8 border border-zinc-100">
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Jam Operasional</h4>
                <p className="text-sm font-medium text-zinc-700">Senin - Jumat</p>
                <p className="text-2xl font-bold text-black mb-4">09:00 - 17:00 WIB</p>
                <p className="text-xs text-zinc-500 italic">*Silakan buat janji temu sebelum mengunjungi pabrik.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Full Width Map */}
      <section className="w-full h-[60vh] border-t border-zinc-200 relative bg-zinc-100">
        <div className="absolute top-6 left-6 z-10 bg-white px-4 py-2 text-[10px] font-bold uppercase tracking-widest shadow-sm border border-zinc-100">
          Global Positioning
        </div>
        {renderMap()}
      </section>

    </div>
  );
};

export default Contact;